/* ==========================================================================
   PDP bundle — "frequently bought together".

   Vanilla ES2019, no dependencies, loaded with defer.

   Each companion row adds ONLY itself, via the round button on that row —
   matching the reference module. There is no "add the whole set" action and the
   main product is not listed, so nothing here reads the main form's variant.

   A companion with several variants exposes a <select>; its chosen option is
   read at click time, so the row always adds what is currently selected.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '[data-bundle]';

  /* ---------------------------------------------------------------------
     Money — mirrors Shopify's money_format placeholders so a variant's price
     renders in the shop's own format instead of a hardcoded one.
     --------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;

    function group(number, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(number) || number === null) number = 0;
      var parts = (number / 100.0).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = group(cents, 0); break;
      case 'amount_with_comma_separator': value = group(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = group(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = group(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = group(cents, 0, ' ', ','); break;
      default: value = group(cents, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  /* ---------------------------------------------------------------------
     One bundle instance
     --------------------------------------------------------------------- */

  function Bundle(root) {
    this.root = root;
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    this.errorLabel = root.getAttribute('data-label-error') || '';

    this.errorEl = root.querySelector('[data-bundle-error]');

    this.bindSelects();
    this.bindOptionPills();
    this.bindItemAdds();
  }

  /** Every companion control, whether a <select> or the hidden single-variant span. */
  Bundle.prototype.controls = function () {
    return Array.prototype.slice.call(this.root.querySelectorAll('[data-bundle-variant]'));
  };

  /** {id, price} for one control. */
  Bundle.prototype.readControl = function (el) {
    if (el.tagName === 'SELECT') {
      var opt = el.options[el.selectedIndex];
      if (!opt || opt.disabled) return null;
      return { id: Number(opt.value), price: Number(opt.getAttribute('data-price') || 0) };
    }
    return {
      id: Number(el.getAttribute('data-value')),
      price: Number(el.getAttribute('data-price') || 0)
    };
  };

  Bundle.prototype.bindSelects = function () {
    var self = this;
    this.controls().forEach(function (el) {
      if (el.tagName !== 'SELECT') return;
      el.addEventListener('change', function () {
        // Keep the per-item price label in step with the chosen variant.
        var item = el.closest('[data-bundle-item]');
        var priceEl = item && item.querySelector('[data-bundle-item-price]');
        var picked = self.readControl(el);
        if (priceEl && picked) priceEl.textContent = formatMoney(picked.price, self.moneyFormat);
      });
    });
  };

  /* Companion variant pills.

     Rendered only for a companion at or under the row's inline limit; past it
     the row links to the product instead and there is nothing here to bind.

     The pills are buttons with role="radio" rather than real radios, because
     this markup sits inside the main product's <form> and a named control there
     would post with the parent add-to-cart.

     The chosen id and price are written onto the hidden [data-bundle-variant]
     span, so readControl() and bindItemAdds() need no branch of their own. */
  Bundle.prototype.bindOptionPills = function () {
    var self = this;

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-bundle-options]'), function (group) {
      var pills = Array.prototype.slice.call(group.querySelectorAll('[data-bundle-option]'));
      var item = group.closest('[data-bundle-item]');
      var store = item && item.querySelector('[data-bundle-variant]');
      var priceEl = item && item.querySelector('[data-bundle-item-price]');
      if (!store || !pills.length) return;

      function select(pill) {
        if (!pill || pill.disabled) return;
        pills.forEach(function (p) {
          var on = p === pill;
          p.setAttribute('aria-checked', on ? 'true' : 'false');
          p.tabIndex = on ? 0 : -1;
        });
        store.setAttribute('data-value', pill.getAttribute('data-value'));
        store.setAttribute('data-price', pill.getAttribute('data-price'));
        if (priceEl) {
          priceEl.textContent = formatMoney(Number(pill.getAttribute('data-price') || 0), self.moneyFormat);
        }
      }

      pills.forEach(function (pill, index) {
        pill.addEventListener('click', function () { select(pill); });

        // Arrow keys move within a radiogroup; that is the pattern's contract.
        pill.addEventListener('keydown', function (event) {
          var step = 0;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
          else return;
          event.preventDefault();
          var next = pill;
          for (var n = 1; n <= pills.length; n++) {
            var candidate = pills[(index + step * n + pills.length * n) % pills.length];
            if (candidate && !candidate.disabled) { next = candidate; break; }
          }
          select(next);
          next.focus();
        });
      });
    });
  };

  Bundle.prototype.bindItemAdds = function () {
    var self = this;

    this.controls().forEach(function (control) {
      var item = control.closest('[data-bundle-item]');
      var button = item && item.querySelector('[data-bundle-item-add]');
      if (!button) return;

      button.addEventListener('click', function (event) {
        event.preventDefault();
        if (button.getAttribute('aria-busy') === 'true') return;

        var picked = self.readControl(control);
        if (!picked || !picked.id) {
          if (self.errorEl) self.errorEl.textContent = self.errorLabel;
          return;
        }

        if (self.errorEl) self.errorEl.textContent = '';
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;

        fetch(cartUrl('/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: picked.id, quantity: 1 }] })
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(function () {
            button.classList.add('is-added');
            self.refreshCartCount();
            // Same contract the PDP uses: the theme re-renders its cart and
            // opens its own drawer. We do not build one.
            document.documentElement.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true, detail: { variant: { id: picked.id }, quantity: 1 }
            }));
          })
          .catch(function (err) {
            var message = (err && (err.description || err.message)) || self.errorLabel;
            if (self.errorEl) self.errorEl.textContent = message;
          })
          .finally(function () {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          });
      });
    });
  };

  Bundle.prototype.refreshCartCount = function () {
    fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var nodes = document.querySelectorAll('[data-cart-count], .Header__CartCount, .CartCount');
        Array.prototype.forEach.call(nodes, function (n) { n.textContent = cart.item_count; });
      })
      .catch(function () {});
  };

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function init() {
    var roots = document.querySelectorAll(ROOT);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.bundleReady === 'true') return;
      root.dataset.bundleReady = 'true';
      new Bundle(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
